import type { AccountStatus, Address, Client, Office, User } from './account';
import type { ProEstItem } from './proEst';
import type { FeeType, PhaseTypeKey, ProjectStatus, ProjectType } from './projectShare';

export * from './projectShare';

export interface ProjectName {
  id?: string;
  status?: ProjectStatus;
  name?: string;
}

export interface Exclusion {
  id: string;
  label: string;
  details?: string;
  category?: string;
}

export interface TermsAndConditions {
  paymentTermsId: string;
  customTerms?: string;
  governingLaw?: string;
}

export interface Project extends ProjectName {
  statusHistory?: ProjectStatusHistory[];
  constructionType?: string;
  deliveryMethod?: string;
  constructionTypeIds?: string[];
  deliveryMethodIds?: string[];
  procurementMethod?: string;
  estimateType?: string;
  projectType?: ProjectType;
  account?: ProjectAccount;
  projectManager?: string;
  providerProjectManager?: string;
  provider?: ProjectAccount;
  drawingName?: string;
  office?: Office;
  providerOffice?: Office;
  client?: Client;
  location?: Address;
  description?: string;
  projectNumber?: string;
  taskOrderNumber?: string;
  budget?: Budget;
  phases?: Phase[];
  users?: User[];
  providerPmUserId?: string;
  createdAt?: string;
  createdBy?: User;
  updatedAt?: string;
  updatedBy?: User;
  submittedAt?: string;
  feeGeneratedAt?: string;
  feeExpiresAt?: string;
  feeApprovedAt?: string;
  estimationTool?: 'proest' | 'excel';
  exclusions?: Exclusion[];
  terms?: TermsAndConditions;
  startsAt?: string;
}

export interface ProjectStatusHistory {
  byUserId: string;
  createdAt: string;
  metadata: Record<string, unknown>;
  newStatus: ProjectStatus;
  oldStatus: ProjectStatus;
  /**
   * Which act this was, where the status alone is ambiguous. Absent on every entry written before
   * the server started recording it, which is honest: the status was all it recorded.
   */
  transition?: string;
}

export interface ProjectAccount {
  id?: string;
  companyName?: string;
  logoUrl?: string;
  status?: AccountStatus;
}

export interface Budget {
  isFixed?: boolean;
  fixed?: number;
  range?: string;
}

export interface ExcelEstimateDetails {
  items?: ProEstItem[];
  total: number;
  totalHours: number;
  createdById?: string;
  createdAt: string;
  originalFileName?: string;
}

export interface Phase {
  id?: string;
  status?: ProjectStatus;
  statusHistory?: ProjectStatusHistory[];
  name?: string;
  description?: string;
  phaseType?: PhaseTypeKey;
  sequence?: number;
  submissionDate?: string;
  deliveryDate?: string;
  users?: User[];
  estimates?: Estimate[];
  boeId?: string;
  fee?: number;
  feeWeight?: number;
  manualFee?: number;
  manualFeeWeight?: number;
  /** The SERVICE fee a counter settled on. The tech fee goes on top of it, not inside it. */
  negotiatedFee?: number;
  /**
   * When the provider reopened this phase's worksheet after its fee had been sent.
   *
   * A marker rather than a status change: the phase keeps its status, its agreed fee and its place
   * in backlog reporting, and this says only that the work may be re-planned. Absent on a phase
   * nobody has reopened, and cleared by the server when the revised fee is sent.
   */
  reopenedAt?: string;
  feeType?: FeeType;
  feeExpires?: string;
  generatedTotals?: PhaseTotals;
  manualTotals?: PhaseTotals;
  negotiatedTotals?: PhaseTotals;
  variations?: PhaseVariations[];
  /**
   * The hours the phase fee pays for - the budget the worksheet draws down from, not a plan for it.
   * Absent on a phase seeded before the budget model, which then falls back to its own totals.
   */
  calculatedHours?: number;
  /** The labor budget the formula arrived at. */
  calculatedLabor?: number;
  /** What the client is quoted: the labor budget plus its tech fee. */
  calculatedFee?: number;
  otherFees?: OtherFee[];
  providerFee?: number;
  /**
   * The tech fee somebody set by hand, where somebody has. Its absence is what makes the fee the
   * server's to keep in line with the phase's labor.
   */
  manualProviderFee?: number;
  proEst?: {
    id: number;
    createdAt: string;
    createdById: string;
  };
  pages?: number;
  unclassifiedPages?: number;
  manualEstimate?: {
    total?: number;
    createdById?: string;
    createdAt?: string;
    scopeDate?: string;
  };
  /** The software this estimate was built in. */
  estimatingTool?: { id?: string; label?: string; version?: string; source?: string };
  /** The cost books and databases this estimate was priced against. */
  costDataSources?: { id?: string; label?: string; year?: string; source?: string }[];
  excelEstimate?: ExcelEstimateDetails;
  invoiceId?: string;
  invoicePaid?: boolean;
  invoicePaidAt?: string;
  paymentTransactionId?: string;
  lastUserUpdatedAt?: string;
}

export interface PhaseVariations {
  description?: string;
}

export interface PhaseTotals {
  cost: number;
  margin: number;
  otherFeeTotal: number;
  hours: number;
  pages: number;
  subtotal: number;
  total: number;
  hourlyRate: number;
}

export interface Estimate {
  id: string;
  parentId?: string;
  children?: Estimate[];
  /** The rate-card record this row was seeded from, which is what identifies it. */
  laborRateId?: string;
  /** The firm's own name for the discipline. Absent falls back to the discipline's label. */
  disciplineName?: string;
  discipline: string;
  baseRate: number;
  levelOfEffortHours: number;
  manualBaseRate: number;
  manualLevelOfEffortHours: number;
  negotiatedBaseRate: number;
  negotiatedLevelOfEffortHours: number;
  otherFees: OtherFee[];
  difficulty: number;
  pages: number;
  generatedTotals?: EstimateTotals;
  manualTotals?: EstimateTotals;
  negotiatedTotals?: EstimateTotals;
  labor?: EstimateLabor[];
  updatedAt?: string;
  lastUserUpdatedAt?: string;
}

export interface EstimateLabor {
  id: string | null;
  userId: string | null;
  user?: User;
  rate: number;
  hours?: number;
  split?: number;
  cost: number;
  margin: number;
  skills?: string[];
  status: ProjectStatus;
  statusHistory: ProjectStatusHistory[];
  createdAt?: string;
  createdById?: string;
  updatedAt?: string;
}

export interface EstimateTotals {
  difficulty: number;
  providerBaseRate: number;
  providerRate: number;
  cost: number;
  margin: number;
  otherFeeTotal: number;
  hourlyRate: number;
  hours: number;
  pages: number;
  subtotal: number;
  total: number;
}

export interface OtherFee {
  name: string;
  description?: string;
  amount?: number;
  rate?: number;
  hours?: number;
}

export interface ProjectCounts {
  accountId: string;
  providerId: string;
  officeId?: string;
  total: number;
  draft: number;
  submitted: number;
  accepted: number;
  feeReady: number;
  feeCounter: number;
  approved: number;
  inProgress: number;
  complete: number;
  rejected: number;
  canceled: number;
}
